"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Container,
  Card,
  CardBody,
  HStack,
  Divider,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const InviteAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const email = searchParams.get("email") || "";
  const companyName = searchParams.get("company") || "";
  const token = searchParams.get("token") || "";

  useEffect(() => {
    if (!email || !companyName || !token) {
      setError("Invalid invitation link. Please contact your administrator.");
    }
  }, [email, companyName, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const { error } = await acceptInvite(email, password, token);

      if (error) {
        setError(error.message);
      } else {
        navigate("/app");
      }
    } catch (err: any) {
      setError(
        err.message || "An error occurred while setting up your account"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!email || !companyName || !token) {
    return (
      <Box
        minH="100vh"
        bg="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card boxShadow="xl" border="1px" borderColor="gray.200">
            <CardBody p={8}>
              <VStack spacing={6}>
                <Box
                  as="img"
                  src={process.env.REACT_APP_MAIN_LOGO_URL}
                  alt="App Logo"
                  h="100px"
                />
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  Invalid invitation link. Please contact your administrator.
                </Alert>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      bg="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="md">
        <Card boxShadow="xl" border="1px" borderColor="gray.200">
          <CardBody p={8}>
            <VStack spacing={6}>
              <Box textAlign="center">
                <Box
                  as="img"
                  src={process.env.REACT_APP_MAIN_LOGO_URL}
                  alt="App Logo"
                  h="100px"
                  mx="auto"
                  mb={2}
                />
                <Text color="secondary.600" fontSize="sm">
                  Complete Your Account Setup
                </Text>
              </Box>

              <Box w="100%" bg="gray.50" p={4} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text
                      fontSize="sm"
                      color="secondary.600"
                      fontWeight="medium"
                    >
                      Company:
                    </Text>
                    <Text
                      fontSize="sm"
                      color="secondary.800"
                      fontWeight="semibold"
                    >
                      {companyName}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text
                      fontSize="sm"
                      color="secondary.600"
                      fontWeight="medium"
                    >
                      Email:
                    </Text>
                    <Text
                      fontSize="sm"
                      color="secondary.800"
                      fontWeight="semibold"
                    >
                      {email}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              <Box w="100%">
                <Text mb={4} color="secondary.700" fontWeight="medium">
                  Set up your password to complete registration:
                </Text>

                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel color="secondary.700">Password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          bg="white"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "primary.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-primary-500)",
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="secondary.700">
                        Confirm Password
                      </FormLabel>
                      <InputGroup>
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          bg="white"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "primary.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-primary-500)",
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showConfirmPassword
                                ? "Hide password"
                                : "Show password"
                            }
                            icon={
                              showConfirmPassword ? (
                                <ViewOffIcon />
                              ) : (
                                <ViewIcon />
                              )
                            }
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      isLoading={isLoading}
                      loadingText="Setting up account..."
                      w="100%"
                      variant="brandGradient"
                      size="lg"
                    >
                      Complete Setup
                    </Button>
                  </VStack>
                </form>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default InviteAccept;
